import { useState } from "react";
import { Truck, Clock, Shield, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMediaQuery, useTheme } from "@mui/material";
import { WhatsApp } from "@mui/icons-material";

const DeliverySection = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const features = [
    {
      icon: Truck,
      title: t("No delivery fees in Qatar"),
      description: t("Free shipping on all orders in Qatar"),
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Clock,
      title: t("Delivery in 24 to 48 hours"),
      description: t("Lightning fast service"),
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: Shield,
      title: t("Secure & Insured"),
      description: t("100% protection guarantee"),
      color: "from-emerald-500 to-teal-500",
    },
  ];

  return (
    <section className="min-h-screen bg-slate-900 py-20">
      <div className="container mx-auto px-6">
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-4 px-4 py-4 bg-[#0d223f] rounded-full border border-[#0d223f]">
              <span className="text-xl font-medium text-white">
                {t("Premium Home Delivery")}
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-2xl lg:text-4xl font-bold leading-tight text-white">
              {t("Shopping from ")}
              <span className="text-blue-400">{t("the comfort of ")}</span>
              {t("your own home")}
            </h1>

            {/* Description */}
            <p className="text-xl text-slate-300 leading-relaxed">
              {t(
                "Allow our team to bring our products to you for you to preview and try for yourself",
              )}
            </p>
          </div>

          {/* Right Content - Van Image */}
          <div className="relative">
            <div className="bg-slate-800 rounded-3xl overflow-hidden border border-slate-700">
              <img
                src="https://cdn1.zohoecommerce.com/Van-.webp?storefront_domain=www.maagoud.qa"
                alt="Delivery Van"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* 1 WhatsApp CTA */}
        <div className="mt-6 mb-10">
          {/* Heading */}
          <h2 className="text-2xl font-bold mb-6 text-left text-white">
            {t("Contact us")}
          </h2>

          <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4`}>
            {/* 1st WhatsApp CTA */}
            <a
              href="https://wa.me/51777105"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-3 bg-green-600 rounded-2xl text-white justify-between font-semibold hover:bg-green-700 transition-colors"
            >
              <WhatsApp fontSize="large" />
              <span className="text-lg font-bold tracking-wider">51777105</span>
              <ChevronRight className="w-8 h-8" />
            </a>

            {/* 2nd WhatsApp CTA */}
            <a
              href="https://wa.me/51777107"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-3 bg-green-600 rounded-2xl text-white justify-between font-semibold hover:bg-green-700 transition-colors"
            >
              <WhatsApp fontSize="large" />
              <span className="text-lg font-bold tracking-wider">51777107</span>
              <ChevronRight className="w-8 h-8" />
            </a>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-slate-800/50 rounded-3xl p-4 border border-slate-700 hover:border-slate-600 transition-colors"
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="flex gap-4">
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  {/* Title + Description */}
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-300 text-lg">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DeliverySection;
